/**
 * Layout 2 -- columns: pick a group, then a unit, then a client, then an
 * engagement; each column lists what lies directly beneath the pick before it.
 * Picking opens the docked detail panel the page renders beside the columns.
 */

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { ContextTag } from './OverviewParts';
import { CHILD_LABEL, subtitleOf, type Level, type OrgNode } from './overviewModel';
import type { ViewProps } from './viewProps';

const COLUMNS: readonly { level: Level; title: string; empty: string }[] = [
  { level: 'group', title: 'Groups', empty: '' },
  { level: 'unit', title: CHILD_LABEL.group, empty: 'Select a group to see its business units.' },
  { level: 'client', title: CHILD_LABEL.unit, empty: 'Select a business unit to see its clients.' },
  { level: 'engagement', title: CHILD_LABEL.client, empty: 'Select a client to see its engagements.' },
];

export function ColumnsView({ roots, onSelect }: ViewProps) {
  // The picks, top-down. A pick that a new search filtered away simply stops
  // matching and its columns go back to their prompt.
  const [picks, setPicks] = useState<readonly string[]>(() => (roots[0] ? [roots[0].id] : []));

  const lists: (readonly OrgNode[] | null)[] = [roots];
  const picked: (OrgNode | undefined)[] = [];
  COLUMNS.forEach((_, index) => {
    const found = lists[index]?.find((candidate) => candidate.id === picks[index]);
    picked.push(found);
    lists.push(found ? found.children : null);
  });

  return (
    <div className="card ov-mill" role="group" aria-label="Organization columns">
      {COLUMNS.map((column, index) => {
        const items = lists[index];
        return (
          <div className="ov-mcol" key={column.level}>
            <div className="ov-mcol-h">
              <b>{column.title}</b>
              <span>{items ? items.length : ''}</span>
            </div>
            {items ? (
              <ul className="ov-mlist">
                {items.map((item) => {
                  const selected = picked[index]?.id === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="ov-mitem"
                        aria-pressed={selected}
                        onClick={() => {
                          setPicks([...picks.slice(0, index), item.id]);
                          onSelect(item.id);
                        }}
                      >
                        <span className="t">
                          {item.name} <ContextTag node={item} />
                        </span>
                        <span className="sub">{subtitleOf(item)}</span>
                        {item.children.length > 0 && (
                          <span className="chev" aria-hidden="true">
                            <ChevronRight />
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="ov-mempty">{column.empty}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
